/* so-reviews.js — progressive enhancement for the PDP review module.
 *
 * The default view is fully server-rendered (snippets/so-reviews.liquid), so this file
 * only adds: read-more toggles, the photo lightbox, sorting, and "Show more".
 *
 * Data source: Judge.me's public JSON endpoint on cdn.judge.me, which returns the same
 * schema as the `judgeme.review_widget_data` metafield we server-render from, with ONE
 * exception: the API omits `body` and supplies only `body_html` (see plainBody()).
 * ⚠️ Reviews are on cdn.judge.me. api.judge.me/api/reviews/* returns 404.
 *
 * SECURITY: review content is third-party user-generated. Cards are built from a STATIC
 * template (no interpolation) and every value from the API is injected via textContent /
 * setAttribute — never via innerHTML string concatenation. Image URLs are additionally
 * protocol-checked. Newlines render via CSS `white-space: pre-line`, so no <br> injection
 * is needed anywhere.
 */
(function () {
  if (window.__soReviewsInit) return;
  window.__soReviewsInit = true;

  var ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
  var PRODUCT_TITLE = (document.querySelector('h1') || {}).textContent || 'this product';
  PRODUCT_TITLE = String(PRODUCT_TITLE).trim();
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var CHECK_SVG = 'M8.6 13.4L5.2 10l1.3-1.3 2.1 2.1 4.9-4.9L14.8 7z';

  /* Static card skeleton — contains NO user data. Slots are filled via textContent. */
  var CARD_TPL =
    '<div class="so-rv__card-head">' +
      '<span class="so-rv__avatar" aria-hidden="true" data-s="initial"></span>' +
      '<span class="so-rv__who">' +
        '<span class="so-rv__name" data-s="name"></span>' +
        '<span class="so-rv__meta">' +
          '<span class="so-rv-verified" data-s="verified" hidden>' +
            '<svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
            '<path d="' + CHECK_SVG + '"></path></svg> Verified</span>' +
          '<span data-s="loc" hidden></span>' +
          '<span data-s="date"></span>' +
        '</span>' +
      '</span>' +
      '<span class="so-rv-stars" role="img" data-s="stars">★★★★★</span>' +
    '</div>' +
    '<h3 class="so-rv__title" data-s="title" hidden></h3>' +
    '<div class="so-rv__body" data-so-body data-s="body" hidden></div>' +
    '<button type="button" class="so-rv__more" data-so-readmore data-s="readmore" hidden>Read more</button>' +
    '<span class="so-rv__variant" data-s="variant" hidden></span>' +
    '<div class="so-rv__photos" data-s="photos" hidden></div>' +
    '<div class="so-rv__reply" data-s="reply" hidden>' +
      '<b data-s="replyname"></b><span data-s="replybody"></span>' +
    '</div>' +
    '<div class="so-rv__helpful" data-s="helpful" hidden>' +
      '<span>Helpful?</span>' +
      '<span class="so-rv__vote" data-s="tu"></span>' +
      '<span class="so-rv__vote" data-s="td"></span>' +
    '</div>';

  function safeUrl(u) {
    var s = String(u == null ? '' : u).trim();
    return /^(https:|http:|\/\/)/i.test(s) ? s : '';
  }
  function fmtDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return String(d.getDate()).padStart(2, '0') + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }
  function cleanLoc(loc) { return String(loc == null ? '' : loc).replace(/[()]/g, '').trim(); }
  function show(el) { if (el) el.hidden = false; }

  /* ⚠️ SCHEMA DIFFERENCE between the two sources — do not "simplify" this away.
     The `judgeme.review_widget_data` METAFIELD (used for SSR) has BOTH `body` (plain text)
     and `body_html`. The cdn.judge.me API response has NO `body` key at all — only
     `body_html`. Verified 2026-07-25: without this fallback every JS-loaded review
     rendered with an empty body.
     We convert to plain text via an INERT DOMParser document and read textContent only —
     the HTML is never inserted into the live DOM, so there is no injection path. */
  function plainBody(r) {
    if (r.body != null && String(r.body).trim()) return String(r.body).trim();
    var html = r.body_html;
    if (!html) return '';
    try {
      var doc = new DOMParser().parseFromString(String(html), 'text/html');
      return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
    } catch (e) { return ''; }
  }

  function buildCard(r) {
    var li = document.createElement('li');
    li.className = 'so-rv__card';
    li.innerHTML = CARD_TPL; // static template only
    var q = function (n) { return li.querySelector('[data-s="' + n + '"]'); };

    q('initial').textContent = String(r.reviewer_initial || '·');
    q('name').textContent = String(r.reviewer_name || 'Anonymous');
    if (r.verified_buyer) show(q('verified'));

    // P0-2: reviewer location hidden (SA-only store; foreign locations read as "do they ship here?").
    q('date').textContent = fmtDate(r.created_at);

    var rating = Number(r.rating) || 0;
    var stars = q('stars');
    stars.style.setProperty('--rating', String(rating));
    stars.setAttribute('aria-label', rating + ' out of 5 stars');

    if (r.title) { q('title').textContent = String(r.title); show(q('title')); }

    var body = plainBody(r);
    if (body) {
      var b = q('body');
      b.textContent = body; // newlines handled by CSS white-space: pre-line
      show(b);
      if (body.length > 300) { b.classList.add('so-rv__body--clamp'); show(q('readmore')); }
    }

    if (r.product_variant_title) {
      q('variant').textContent = String(r.product_variant_title);
      show(q('variant'));
    }

    var pics = Array.isArray(r.pictures_urls) ? r.pictures_urls.slice(0, 4) : [];
    if (pics.length) {
      var wrap = q('photos');
      pics.forEach(function (p) {
        var big = safeUrl(p.huge || p.original);
        var small = safeUrl(p.small || p.compact || p.original);
        if (!small) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        if (big) btn.setAttribute('data-so-lb', big);
        btn.setAttribute('data-so-lb-cap', String(r.reviewer_name || ''));
        btn.setAttribute('aria-label', 'View photo from ' + String(r.reviewer_name || 'customer'));
        var img = document.createElement('img');
        img.src = small;
        /* R1: mirror the SSR alt text — these are meaningful product photos, not decoration. */
        img.alt = 'Customer photo of ' + PRODUCT_TITLE + ' from ' + (r.reviewer_name || 'a verified buyer');
        img.width = 56; img.height = 56; img.loading = 'lazy';
        btn.appendChild(img);
        wrap.appendChild(btn);
      });
      if (wrap.children.length) show(wrap);
    }

    if (r.reply_content) {
      q('replyname').textContent = String(r.shop_reply_name || 'Smoke Organic Store') + ' replied';
      q('replybody').textContent = String(r.reply_content);
      show(q('reply'));
    }

    var tu = Number(r.thumb_up) || 0, td = Number(r.thumb_down) || 0;
    if (tu > 0 || td > 0) {
      q('tu').textContent = '▲ ' + tu;
      q('td').textContent = '▼ ' + td;
      show(q('helpful'));
    }
    return li;
  }

  var section = document.getElementById('so-reviews');
  if (!section) return;
  var root = section.querySelector('[data-so-reviews]');
  var list = section.querySelector('[data-so-list]');
  var moreBtn = section.querySelector('[data-so-more]');
  var progress = section.querySelector('[data-so-progress]');
  var sortSel = section.querySelector('[data-so-sort]');
  var chips = [].slice.call(section.querySelectorAll('[data-so-kw]'));
  var clearBtn = section.querySelector('[data-so-clear]');
  var histRows = [].slice.call(section.querySelectorAll('[data-so-rating]'));
  var tabTitle = section.querySelector('[data-so-tabtitle]');
  var grandTotal = root ? parseInt(root.dataset.total, 10) || 0 : 0;

  var state = {
    page: 1,
    sortBy: 'created_at',
    sortDir: 'desc',
    keyword: '',
    rating: '',
    total: root ? parseInt(root.dataset.total, 10) || 0 : 0,
    totalPages: root ? parseInt(root.dataset.totalPages, 10) || 1 : 1,
    shown: list ? list.children.length : 0,
    loading: false
  };

  function url(page) {
    var q = new URLSearchParams({
      product_id: root.dataset.productId,
      page: String(page),
      sort_by: state.sortBy,
      sort_dir: state.sortDir,
      translation_locale: 'en',
      skip_other_languages: 'true',
      widget_theme: 'standard',
      ts: root.dataset.ts || '',
      shop_domain: root.dataset.shopDomain,
      platform: 'shopify'
    });
    /* The endpoint filters server-side on `keyword` and its counts match the chip
       counts exactly (verified 2026-07-25). Filtered responses report total_pages:1. */
    if (state.keyword) q.set('keyword', state.keyword);
    /* Histogram filter. `filter_rating` is the correct param — `rating`, `stars`,
       `filter_by_rating` etc. are all silently IGNORED (returning the unfiltered list,
       which looks like it works). Verified against the histogram on the 313 e-liquid.
       Combines cleanly with `keyword`. */
    if (state.rating) q.set('filter_rating', state.rating);
    return ENDPOINT + '?' + q.toString();
  }

  function setProgress() {
    var filtered = state.keyword || state.rating;
    if (progress) {
      if (filtered) {
        var bits = [];
        if (state.rating) bits.push(state.rating + '-star');
        if (state.keyword) bits.push('mentioning “' + state.keyword + '”');
        progress.textContent = 'Showing ' + state.shown + ' of ' + state.total +
          ' review' + (state.total === 1 ? '' : 's') + ' ' + bits.join(', ');
      } else {
        progress.textContent = 'Showing ' + state.shown + ' of ' + state.total + ' reviews';
      }
    }
    if (tabTitle) {
      tabTitle.textContent = filtered
        ? 'Reviews (' + state.total + ' of ' + grandTotal + ')'
        : 'Reviews (' + state.total + ')';
    }
    if (clearBtn) clearBtn.hidden = !filtered;
    if (moreBtn) moreBtn.hidden = state.page >= state.totalPages || state.shown >= state.total;
  }

  function fetchPage(page, replace) {
    if (state.loading || !root || !list) return;
    state.loading = true;
    /* Visible feedback matters: on a product where every review is 5 stars, sorting by
       rating legitimately cannot reorder anything (Nasty 9k is 58/58 five-star), so
       without this the control looks broken (Charl reported exactly that). This fade +
       the updated count is the whole feedback mechanism — we deliberately do NOT scroll. */
    if (replace) list.classList.add('is-loading');
    if (moreBtn) { moreBtn.disabled = true; moreBtn.textContent = 'Loading…'; }

    fetch(url(page), { credentials: 'omit' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(function (d) {
        var revs = Array.isArray(d.reviews) ? d.reviews : [];
        var frag = document.createDocumentFragment();
        revs.forEach(function (r) { frag.appendChild(buildCard(r)); });
        if (replace) {
          list.textContent = '';
          if (!revs.length) {
            var li = document.createElement('li');
            li.className = 'so-rv__empty-filter';
            var what = [];
            if (state.rating) what.push(state.rating + '-star');
            if (state.keyword) what.push('mentioning “' + state.keyword + '”');
            li.textContent = what.length
              ? 'No ' + what.join(' reviews ') + ' reviews to show.'
              : 'No reviews to show.';
            list.appendChild(li);
          } else {
            list.appendChild(frag);
          }
          state.shown = revs.length;
        } else { list.appendChild(frag); state.shown += revs.length; }
        state.page = (d.pagination && d.pagination.current_page) || page;
        state.totalPages = (d.pagination && d.pagination.total_pages) || 1;
        if (state.rating && !state.keyword) {
          /* Exact count is already on the page in the histogram, and is more accurate than
             revs.length when the filtered set spans multiple pages (5-star = 120 over 15). */
          var row = section.querySelector('[data-so-rating="' + state.rating + '"] .so-rv__hist-n');
          state.total = row ? (parseInt(row.textContent, 10) || revs.length) : revs.length;
        } else if (state.keyword) {
          state.total = state.shown;
        } else {
          state.total = grandTotal;
        }
        setProgress();
        /* Deliberately NO scrollIntoView here. Yanking the viewport on every sort/filter
           click is disorienting (Charl, 2026-07-25) — the .is-loading fade plus the
           updated count/status line are enough feedback, and the list top is already
           in view when the controls are. */
      })
      .catch(function () {
        if (progress) progress.textContent = 'Couldn’t load more reviews just now — please try again.';
      })
      .finally(function () {
        state.loading = false;
        list.classList.remove('is-loading');
        if (moreBtn) { moreBtn.disabled = false; moreBtn.textContent = 'Show more reviews'; }
      });
  }

  if (moreBtn) moreBtn.addEventListener('click', function () { fetchPage(state.page + 1, false); });

  if (sortSel) {
    /* Browsers restore <select> state on reload/back-navigation, so the control could say
       "Lowest rating" while the list below is the server-rendered most-recent page — a
       silent lie about what's displayed (seen in preview 2026-07-25). The SSR list is
       always created_at/desc, so reset the control to match it on init. */
    if (sortSel.value !== 'created_at|desc') sortSel.value = 'created_at|desc';

    sortSel.addEventListener('change', function () {
      var parts = String(sortSel.value).split('|');
      state.sortBy = parts[0] || 'created_at';
      state.sortDir = parts[1] || 'desc';
      state.page = 1;
      fetchPage(1, true);
    });
  }

  /* ---------- keyword chip filters ---------- */
  function applyKeyword(kw) {
    state.keyword = kw || '';
    applyFilters();
  }
  chips.forEach(function (c) {
    c.addEventListener('click', function () {
      var kw = c.getAttribute('data-so-kw');
      applyKeyword(c.getAttribute('aria-pressed') === 'true' ? '' : kw); // click again to unset
    });
  });
  /* ---------- histogram disclosure (R8) ----------
     The bars double as the rating filters, so this label is also what makes the filtering
     discoverable (R3) — previously the rows were buttons that looked like decoration. */
  var histToggle = section.querySelector('[data-so-hist-toggle]');
  var histWrap = section.querySelector('#so-rv-hist');
  if (histToggle && histWrap) {
    histToggle.addEventListener('click', function () {
      var open = histToggle.getAttribute('aria-expanded') === 'true';
      histToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      histWrap.hidden = open;
    });
  }
  function openHist() {
    if (histToggle && histWrap && histWrap.hidden) {
      histToggle.setAttribute('aria-expanded', 'true');
      histWrap.hidden = false;
    }
  }

  /* ---------- histogram rating filters ---------- */
  function syncPressed() {
    chips.forEach(function (c) {
      c.setAttribute('aria-pressed', c.getAttribute('data-so-kw') === state.keyword ? 'true' : 'false');
    });
    histRows.forEach(function (r) {
      r.setAttribute('aria-pressed', r.getAttribute('data-so-rating') === state.rating ? 'true' : 'false');
    });
  }
  function applyFilters() { state.page = 1; syncPressed(); fetchPage(1, true); }

  histRows.forEach(function (r) {
    if (r.disabled) return;
    r.addEventListener('click', function () {
      var star = r.getAttribute('data-so-rating');
      state.rating = (state.rating === star) ? '' : star; // click again to unset
      openHist(); // never collapse the panel out from under an active filter
      applyFilters();
    });
  });

  if (clearBtn) clearBtn.addEventListener('click', function () {
    state.keyword = ''; state.rating = ''; applyFilters();
  });

  /* ---------- read more (delegated) ---------- */
  section.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-so-readmore]');
    if (!btn) return;
    var card = btn.closest('.so-rv__card');
    var body = card && card.querySelector('[data-so-body]');
    if (!body) return;
    var stillClamped = body.classList.toggle('so-rv__body--clamp');
    btn.textContent = stillClamped ? 'Read more' : 'Show less';
  });

  /* ---------- lightbox ---------- */
  var lb = section.querySelector('[data-so-lightbox]');
  var lbImg = section.querySelector('[data-so-lb-img]');
  var lbCap = section.querySelector('[data-so-lb-caption]');
  var lastFocus = null;

  function closeLb() {
    if (!lb || lb.hidden) return;
    lb.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  section.addEventListener('click', function (e) {
    var t = e.target.closest('[data-so-lb]');
    if (t && lb && lbImg) {
      var src = safeUrl(t.getAttribute('data-so-lb'));
      if (!src) return;
      lastFocus = t;
      lbImg.src = src;
      if (lbCap) lbCap.textContent = t.getAttribute('data-so-lb-cap') || '';
      lb.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var c = lb.querySelector('[data-so-lb-close]');
      if (c) c.focus();
      return;
    }
    if (e.target.closest('[data-so-lb-close]') || e.target === lb) closeLb();
  });

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeLb(); });

  setProgress();
})();
